import React from 'react';
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, TextField, Typography
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { EditDateLimit, EditPharmacy } from './editApi';

/** Выбор аптеки (список уже отфильтрован сервером по области видимости роли) */
export const PharmacyAutocomplete: React.FC<{
  options: EditPharmacy[];
  value: EditPharmacy | null;
  onChange: (value: EditPharmacy | null) => void;
  loading?: boolean;
  disabled?: boolean;
}> = ({ options, value, onChange, loading, disabled }) => (
  <Autocomplete
    options={options}
    value={value}
    onChange={(_, next) => onChange(next)}
    getOptionLabel={(o) => `${o.username} (аптека ${o.pharmacyNum})`}
    isOptionEqualToValue={(a, b) => a.id === b.id}
    loading={loading}
    disabled={disabled}
    loadingText="Загрузка..."
    noOptionsText="Аптеки не найдены"
    size="small"
    sx={{ width: { xs: '100%', sm: 320 } }}
    renderInput={(params) => <TextField {...params} label="Аптека" placeholder="Номер или логин" />}
  />
);

const LIMIT_LABELS: Record<EditDateLimit, string> = {
  any: '',
  yesterday: 'Только вчера',
  last_month: 'Только прошлый месяц'
};

/** Плашка ограничения даты по роли (политика date_limit) */
export const LimitChip: React.FC<{ limit: EditDateLimit }> = ({ limit }) => {
  if (limit === 'any') return null;
  return (
    <Chip
      icon={<LockIcon sx={{ fontSize: '13px !important' }} />}
      label={LIMIT_LABELS[limit]}
      size="small"
      sx={{
        fontSize: '0.72rem',
        fontWeight: 700,
        color: '#b45309',
        backgroundColor: '#fef3c7',
        border: '1px solid #fde68a',
        '& .MuiChip-icon': { color: '#b45309' }
      }}
    />
  );
};

const SCOPE_LABELS: Record<string, string> = {
  all: 'Вся сеть аптек',
  assigned_pharmacies: 'Курируемые аптеки',
  own_region: 'Свой регион'
};

export const ScopeChip: React.FC<{ scope: string }> = ({ scope }) => {
  const label = SCOPE_LABELS[scope];
  if (!label) return null;
  return (
    <Chip
      label={`Область: ${label}`}
      size="small"
      sx={{
        fontSize: '0.72rem',
        fontWeight: 700,
        color: '#475569',
        backgroundColor: '#f1f5f9',
        border: '1px solid #e2e8f0'
      }}
    />
  );
};

/** Заглушка: данные не выбраны, не найдены или не загрузились */
export const EditEmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  text?: string;
  action?: React.ReactNode;
}> = ({ icon, title, text, action }) => (
  <Paper
    elevation={0}
    sx={{ border: '1px dashed #cbd5e1', borderRadius: '12px', py: 6, px: 3, textAlign: 'center', backgroundColor: '#ffffff' }}
  >
    <Box
      sx={{
        width: 52, height: 52, mx: 'auto', mb: 1.5, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#f1f5f9', color: '#94a3b8'
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.98rem' }}>{title}</Typography>
    {text && (
      <Typography sx={{ color: '#64748b', fontSize: '0.86rem', mt: 0.5, maxWidth: 460, mx: 'auto' }}>{text}</Typography>
    )}
    {action && <Box sx={{ mt: 2 }}>{action}</Box>}
  </Paper>
);

/** Диалог подтверждения: светлая шапка, действия справа */
export const ConfirmDialog: React.FC<{
  open: boolean;
  title: string;
  confirmText: string;
  cancelText?: string;
  loading?: boolean;
  /** Кнопка подтверждения недоступна (например, не выбрано ни одной колонки) */
  confirmDisabled?: boolean;
  /** Цвет кнопки подтверждения: `error` - для удаления */
  confirmColor?: 'primary' | 'error';
  maxWidth?: 'xs' | 'sm' | 'md';
  onConfirm: () => void;
  onClose: () => void;
  children: React.ReactNode;
}> = ({
  open, title, confirmText, cancelText = 'Отмена', loading, confirmDisabled, confirmColor = 'primary',
  maxWidth = 'sm', onConfirm, onClose, children
}) => (
  <Dialog
    open={open}
    onClose={loading ? undefined : onClose}
    fullWidth
    maxWidth={maxWidth}
    PaperProps={{ sx: { borderRadius: '12px' } }}
  >
    <DialogTitle
      sx={{
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        fontSize: '1.05rem',
        fontWeight: 700,
        color: '#0f172a'
      }}
    >
      {title}
    </DialogTitle>
    <DialogContent sx={{ px: 3 }}>
      <Box sx={{ pt: 2.5 }}>{children}</Box>
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid #e2e8f0', gap: 1 }}>
      <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none', fontWeight: 600, color: '#475569' }}>
        {cancelText}
      </Button>
      <Button
        variant="contained"
        color={confirmColor}
        onClick={onConfirm}
        disabled={loading || confirmDisabled}
        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none', minWidth: 150 }}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : confirmText}
      </Button>
    </DialogActions>
  </Dialog>
);

/** Строка «поле: было ➔ стало» для подтверждения изменений */
export const ChangeRow: React.FC<{ label: string; from: string; to: string }> = ({ label, from, to }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'baseline', gap: 2, py: 0.75,
      borderBottom: '1px solid #f1f5f9', '&:last-of-type': { borderBottom: 'none' }
    }}
  >
    <Typography sx={{ flex: 1, fontSize: '0.85rem', color: '#334155' }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{from}</Typography>
    <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8' }}>&rarr;</Typography>
    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums', minWidth: 90, textAlign: 'right' }}>
      {to}
    </Typography>
  </Box>
);
